/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { Flex, Timeline, Typography, theme } from "@duxton-web/ui"
import React, { memo } from "react"
import { MavTimeline } from "types/api/incidents"
import dayjs, { Dayjs } from "dayjs"
import createPropsAreEqual from "utils/createPropsAreEqual"
import { orderBy } from "lodash"
import { useLocaleContext } from "common/utils/i18n"
import { getDayjsLocale, isTaiwanLocale } from "utils/i18n"
import { useTimelineActivityCodeContent } from "./incidentContentMapping"

const { useToken } = theme

export const formatIncidentActivityTimestamp = (
  date: Dayjs,
  locale: string,
) => {
  const localizedDate = date.locale(getDayjsLocale(locale))
  if (isTaiwanLocale(locale)) {
    return localizedDate.format("YYYY年M月D日 Ah:mm")
  }
  return localizedDate.format("DD MMM YYYY hh:mm A")
}

function TimelineChildren({ timelineItem }: { timelineItem: MavTimeline }) {
  const { token } = useToken()
  const locale = useLocaleContext("locale")
  const { displayString, activityCode, contentTime } = timelineItem
  let activityContent = useTimelineActivityCodeContent(activityCode)
  if (displayString && !!activityContent) {
    activityContent = activityContent.replace(
      "<currency.amount>",
      displayString,
    )
  }
  return (
    <Flex vertical>
      <Typography.Text>
        <b>{activityContent}</b>
      </Typography.Text>
      <Typography.Text style={{ color: token.colorNeutral }}>
        {formatIncidentActivityTimestamp(dayjs(contentTime), locale)}
      </Typography.Text>
    </Flex>
  )
}

export interface IncidentActivityTimelineProps {
  timeline: MavTimeline[]
}

function IncidentActivityTimeline({ timeline }: IncidentActivityTimelineProps) {
  const sortedTimeline = orderBy(timeline, "contentTime", "asc") // Changed to ascending order to make similar with mex app
  return (
    <Timeline
      items={sortedTimeline.map(timelineItem => ({
        color: "green",
        children: <TimelineChildren timelineItem={timelineItem} />,
      }))}
    />
  )
}

export default memo(IncidentActivityTimeline, createPropsAreEqual(["timeline"]))
